import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Plus, Trash2, Package, Lock, Edit3, Pencil, MessageSquare, Clock, ImageIcon, X, UserCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import ImageUploader from '../components/ImageUploader';

const PropertyOptions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [globalOptions, setGlobalOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDeadlineType, setEditDeadlineType] = useState('none');
  const [editDeadlineValue, setEditDeadlineValue] = useState('');
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  // États pour l'édition des options prestataire
  const [editTitrePersonnalise, setEditTitrePersonnalise] = useState('');
  const [editMontantPrestataire, setEditMontantPrestataire] = useState('');
  const [editPrestataireNom, setEditPrestataireNom] = useState('');
  const [editPrestataireEmail, setEditPrestataireEmail] = useState('');
  const [editPrestataireTelephone, setEditPrestataireTelephone] = useState('');
  const [editPrestataireAdresse, setEditPrestataireAdresse] = useState('');
  const [editNoteInterne, setEditNoteInterne] = useState('');
  // Nouveaux états pour tarification
  const [editTypeTarif, setEditTypeTarif] = useState('personne');
  const [editParticipantsMin, setEditParticipantsMin] = useState('');
  const [editParticipantsMax, setEditParticipantsMax] = useState('');
  
  const [formData, setFormData] = useState({
    global_option_id: '',
    prix: '',
    description_custom: '',
    actif: true,
    deadline_type: 'none',
    deadline_value: '',
    question_text: '',
    image_url: '',
    // Champs prestataire
    titre_personnalise: '',
    montant_prestataire: '',
    prestataire_nom: '',
    prestataire_email: '',
    prestataire_telephone: '',
    prestataire_adresse: '',
    note_interne: '',
    // Tarification
    type_tarif: 'personne',
    participants_min: '',
    participants_max: '',
  });
  const [selectedGlobalOption, setSelectedGlobalOption] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (formData.global_option_id) {
      const globalOpt = globalOptions.find(opt => opt.id === formData.global_option_id);
      setSelectedGlobalOption(globalOpt);
      
      // Si prix fixe défini, pré-remplir
      if (globalOpt?.prix_fixe) {
        setFormData(prev => ({ ...prev, prix: globalOpt.prix_fixe }));
      }
    } else {
      setSelectedGlobalOption(null);
    }
  }, [formData.global_option_id, globalOptions]);

  const fetchData = async () => {
    try {
      const [propertyRes, optionsRes, globalRes] = await Promise.all([
        api.get(`/properties/${id}`),
        api.get(`/properties/${id}/options`),
        api.get('/admin/options'),
      ]);
      setProperty(propertyRes.data);
      setPropertyOptions(optionsRes.data);
      setGlobalOptions(globalRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isPrestataire = selectedGlobalOption?.option_type === 'prestataire';
    
    // Validation du deadline
    if (formData.deadline_type !== 'none' && (!formData.deadline_value || parseInt(formData.deadline_value) <= 0)) {
      toast.error('Veuillez entrer une valeur de délai valide');
      return;
    }
    
    // Validations spécifiques aux options prestataire
    if (isPrestataire) {
      if (!formData.titre_personnalise.trim()) {
        toast.error('Le titre personnalisé est obligatoire pour une option avec prestataire');
        return;
      }
      if (!formData.montant_prestataire || parseFloat(formData.montant_prestataire) < 0) {
        toast.error('Le montant prestataire est obligatoire');
        return;
      }
      const marge = parseFloat(formData.prix) - parseFloat(formData.montant_prestataire);
      if (marge < 5) {
        toast.error('La marge restante doit être d\'au moins 5 € afin de couvrir les frais de paiement et la commission de la plateforme.');
        return;
      }
    }
    
    const payload = {
      global_option_id: formData.global_option_id,
      prix: parseFloat(formData.prix),
      description_custom: selectedGlobalOption?.description_editable ? formData.description_custom : null,
      actif: formData.actif,
      deadline_type: formData.deadline_type === 'none' ? null : formData.deadline_type,
      deadline_value: formData.deadline_type === 'none' ? null : parseInt(formData.deadline_value),
      question_text: formData.question_text.trim() || null,
      image_url: formData.image_url || null,
      // Champs prestataire
      titre_personnalise: isPrestataire ? formData.titre_personnalise.trim() : null,
      montant_prestataire: isPrestataire ? parseFloat(formData.montant_prestataire) : null,
      prestataire_nom: isPrestataire ? formData.prestataire_nom.trim() || null : null,
      prestataire_email: isPrestataire ? formData.prestataire_email.trim() || null : null,
      prestataire_telephone: isPrestataire ? formData.prestataire_telephone.trim() || null : null,
      prestataire_adresse: isPrestataire ? formData.prestataire_adresse.trim() || null : null,
      note_interne: isPrestataire ? formData.note_interne.trim() || null : null,
    };
    
    try {
      await api.post(`/properties/${id}/options`, payload);
      toast.success('Option ajoutée avec succès !');
      setDialogOpen(false);
      setFormData({ 
        global_option_id: '', prix: '', description_custom: '', actif: true, 
        deadline_type: 'none', deadline_value: '', question_text: '', image_url: '',
        titre_personnalise: '', montant_prestataire: '', prestataire_nom: '',
        prestataire_email: '', prestataire_telephone: '', prestataire_adresse: '', note_interne: ''
      });
      setSelectedGlobalOption(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const toggleActif = async (optionId, currentStatus) => {
    try {
      await api.put(`/properties/${id}/options/${optionId}`, { actif: !currentStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (optionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette option ?')) return;
    try {
      await api.delete(`/properties/${id}/options/${optionId}`);
      toast.success('Option supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (option) => {
    const globalOpt = option.global_option || {};
    setEditingOption(option);
    setEditPrice(option.prix.toString());
    setEditDescription(option.description_custom || '');
    setEditDeadlineType(option.deadline_type || 'none');
    setEditDeadlineValue(option.deadline_value ? option.deadline_value.toString() : '');
    setEditQuestionText(option.question_text || '');
    setEditImageUrl(option.image_url || '');
    // Champs prestataire
    setEditTitrePersonnalise(option.titre_personnalise || '');
    setEditMontantPrestataire(option.montant_prestataire ? option.montant_prestataire.toString() : '');
    setEditPrestataireNom(option.prestataire_nom || '');
    setEditPrestataireEmail(option.prestataire_email || '');
    setEditPrestataireTelephone(option.prestataire_telephone || '');
    setEditPrestataireAdresse(option.prestataire_adresse || '');
    setEditNoteInterne(option.note_interne || '');
    // Tarification
    setEditTypeTarif(option.type_tarif || 'personne');
    setEditParticipantsMin(option.participants_min ? option.participants_min.toString() : '');
    setEditParticipantsMax(option.participants_max ? option.participants_max.toString() : '');
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingOption) return;

    const globalOpt = editingOption.global_option || {};
    const isPrixFixe = globalOpt.prix_fixe !== null && globalOpt.prix_fixe !== undefined;
    const isPrestataire = globalOpt.option_type === 'prestataire';

    // Validation du deadline
    if (editDeadlineType !== 'none' && (!editDeadlineValue || parseInt(editDeadlineValue) <= 0)) {
      toast.error('Veuillez entrer une valeur de délai valide');
      return;
    }
    
    // Validations spécifiques aux options prestataire
    if (isPrestataire) {
      if (!editTitrePersonnalise.trim()) {
        toast.error('Le titre personnalisé est obligatoire pour une option avec prestataire');
        return;
      }
      if (!editMontantPrestataire || parseFloat(editMontantPrestataire) < 0) {
        toast.error('Le montant prestataire est obligatoire');
        return;
      }
      const marge = parseFloat(editPrice) - parseFloat(editMontantPrestataire);
      if (marge < 5) {
        toast.error('La marge restante doit être d\'au moins 5 € afin de couvrir les frais de paiement et la commission de la plateforme.');
        return;
      }
    }

    const updates = {};
    
    // Mettre à jour le prix seulement si pas de prix fixe (ou si prestataire)
    if (!isPrixFixe || isPrestataire) {
      updates.prix = parseFloat(editPrice);
    }
    
    // Mettre à jour la description si éditable
    if (globalOpt.description_editable) {
      updates.description_custom = editDescription;
    }

    // Mettre à jour les deadlines
    updates.deadline_type = editDeadlineType === 'none' ? null : editDeadlineType;
    updates.deadline_value = editDeadlineType === 'none' ? null : parseInt(editDeadlineValue);

    // Mettre à jour la question personnalisée
    updates.question_text = editQuestionText.trim() || null;

    // Mettre à jour l'image personnalisée
    updates.image_url = editImageUrl || null;
    
    // Champs prestataire
    if (isPrestataire) {
      updates.titre_personnalise = editTitrePersonnalise.trim();
      updates.montant_prestataire = parseFloat(editMontantPrestataire);
      updates.prestataire_nom = editPrestataireNom.trim() || null;
      updates.prestataire_email = editPrestataireEmail.trim() || null;
      updates.prestataire_telephone = editPrestataireTelephone.trim() || null;
      updates.prestataire_adresse = editPrestataireAdresse.trim() || null;
      updates.note_interne = editNoteInterne.trim() || null;
      // Tarification
      updates.type_tarif = editTypeTarif;
      updates.participants_min = editParticipantsMin ? parseInt(editParticipantsMin) : null;
      updates.participants_max = editParticipantsMax ? parseInt(editParticipantsMax) : null;
    }

    try {
      await api.put(`/properties/${id}/options/${editingOption.id}`, updates);
      toast.success('Option modifiée avec succès !');
      setEditDialogOpen(false);
      setEditingOption(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/properties')}
          data-testid="back-to-properties-btn"
          className="mb-4 sm:mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour
        </Button>

        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{property?.nom}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Gérez les options disponibles</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-property-option-btn" className="w-full sm:w-auto bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ajouter une option
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Ajouter une option</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="option">Option *</Label>
                  <Select
                    value={formData.global_option_id}
                    onValueChange={(value) => setFormData({ ...formData, global_option_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="option-select" className="mt-1">
                      <SelectValue placeholder="Sélectionner une option" />
                    </SelectTrigger>
                    <SelectContent>
                      {globalOptions.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          <div className="flex items-center gap-2">
                            {opt.option_type === 'prestataire' ? (
                              <UserCheck className="w-4 h-4 text-purple-600" />
                            ) : (
                              <Package className="w-4 h-4 text-rose-600" />
                            )}
                            <span>{opt.nom}</span>
                            {opt.prix_fixe && opt.option_type !== 'prestataire' && (
                              <span className="text-xs text-amber-600">({opt.prix_fixe}€)</span>
                            )}
                            {opt.option_type === 'prestataire' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Prestataire</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedGlobalOption?.description_editable && (
                  <div className="p-3 bg-rose-50 rounded-lg">
                    <Label htmlFor="description_custom" className="flex items-center">
                      <Edit3 className="w-4 h-4 mr-2 text-rose-600" />
                      Description personnalisée
                    </Label>
                    <Textarea
                      id="description_custom"
                      value={formData.description_custom}
                      onChange={(e) => setFormData({ ...formData, description_custom: e.target.value })}
                      data-testid="option-description-custom-input"
                      placeholder="Ex: Croissants bio, café équitable, fruits frais de saison"
                      className="mt-2"
                      rows={3}
                    />
                    <p className="text-xs text-slate-600 mt-2">
                      Personnalisez la description pour votre logement
                    </p>
                  </div>
                )}

                {/* Affichage de la question si elle existe */}
                {selectedGlobalOption?.question_enabled && selectedGlobalOption?.question_text && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="flex items-center text-blue-700">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Question posée au voyageur
                    </Label>
                    <p className="mt-2 text-sm text-blue-800 bg-white p-2 rounded border border-blue-100">
                      "{selectedGlobalOption.question_text}"
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      Cette question sera posée au voyageur lors de la sélection de cette option
                    </p>
                  </div>
                )}
                
                {/* Section prestataire si option de type prestataire */}
                {selectedGlobalOption?.option_type === 'prestataire' && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <UserCheck className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-900">Option avec prestataire externe</span>
                    </div>
                    
                    <div>
                      <Label htmlFor="titre_personnalise" className="text-purple-800">
                        Titre affiché au voyageur *
                      </Label>
                      <Input
                        id="titre_personnalise"
                        value={formData.titre_personnalise}
                        onChange={(e) => setFormData({ ...formData, titre_personnalise: e.target.value })}
                        required
                        data-testid="titre-personnalise-input"
                        placeholder="Ex: Accrobranche au parc X"
                        className="mt-1"
                      />
                      <p className="text-xs text-purple-600 mt-1">Ce titre sera affiché au voyageur sur la page de réservation</p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-purple-200">
                      <div>
                        <Label htmlFor="prestataire_nom">Nom du prestataire</Label>
                        <Input
                          id="prestataire_nom"
                          value={formData.prestataire_nom}
                          onChange={(e) => setFormData({ ...formData, prestataire_nom: e.target.value })}
                          data-testid="prestataire-nom-input"
                          placeholder="Ex: Parc Aventure"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="prestataire_email">Email</Label>
                        <Input
                          id="prestataire_email"
                          type="email"
                          value={formData.prestataire_email}
                          onChange={(e) => setFormData({ ...formData, prestataire_email: e.target.value })}
                          data-testid="prestataire-email-input"
                          placeholder="contact@prestataire.fr"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="prestataire_telephone">Téléphone</Label>
                        <Input
                          id="prestataire_telephone"
                          type="tel"
                          value={formData.prestataire_telephone}
                          onChange={(e) => setFormData({ ...formData, prestataire_telephone: e.target.value })}
                          data-testid="prestataire-telephone-input"
                          placeholder="06 12 34 56 78"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="prestataire_adresse">Adresse (facultatif)</Label>
                        <Input
                          id="prestataire_adresse"
                          value={formData.prestataire_adresse}
                          onChange={(e) => setFormData({ ...formData, prestataire_adresse: e.target.value })}
                          data-testid="prestataire-adresse-input"
                          placeholder="Adresse du prestataire"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="note_interne">Note interne (facultatif)</Label>
                      <Textarea
                        id="note_interne"
                        value={formData.note_interne}
                        onChange={(e) => setFormData({ ...formData, note_interne: e.target.value })}
                        data-testid="note-interne-input"
                        placeholder="Ex: Réserver 48h à l'avance. Contact: Marie"
                        className="mt-1"
                        rows={2}
                      />
                      <p className="text-xs text-slate-500 mt-1">Cette note est visible uniquement par vous</p>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="prix" className="flex items-center">
                    {selectedGlobalOption?.option_type === 'prestataire' ? 'Prix payé par le voyageur (€) *' : 'Prix (€) *'}
                    {selectedGlobalOption?.prix_fixe && selectedGlobalOption?.option_type !== 'prestataire' && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        Prix fixe imposé
                      </span>
                    )}
                  </Label>
                  <Input
                    id="prix"
                    type="number"
                    step="0.01"
                    min="5"
                    value={formData.prix}
                    onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                    required
                    disabled={selectedGlobalOption?.prix_fixe && selectedGlobalOption?.option_type !== 'prestataire'}
                    data-testid="option-prix-input"
                    className="mt-1"
                  />
                  {selectedGlobalOption?.prix_fixe && selectedGlobalOption?.option_type !== 'prestataire' ? (
                    <p className="text-xs text-amber-600 mt-2">
                      Le prix de cette option est fixé par l'administration et ne peut pas être modifié
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-2">
                      {selectedGlobalOption?.option_type === 'prestataire' 
                        ? "Prix total que le voyageur va payer"
                        : "Prix minimum : 5€ (pour couvrir les frais de transaction)"}
                    </p>
                  )}
                </div>
                
                {/* Montant prestataire pour les options prestataire */}
                {selectedGlobalOption?.option_type === 'prestataire' && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Label htmlFor="montant_prestataire" className="text-purple-800">
                      Montant dû au prestataire (€) *
                    </Label>
                    <Input
                      id="montant_prestataire"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.montant_prestataire}
                      onChange={(e) => setFormData({ ...formData, montant_prestataire: e.target.value })}
                      required
                      data-testid="montant-prestataire-input"
                      className="mt-1"
                    />
                    
                    {/* Calcul automatique de la marge */}
                    {formData.prix && formData.montant_prestataire && (
                      <div className="mt-3 p-3 bg-white rounded border border-purple-100">
                        {(() => {
                          const prix = parseFloat(formData.prix) || 0;
                          const montantPresta = parseFloat(formData.montant_prestataire) || 0;
                          const marge = prix - montantPresta;
                          const pctConcierge = selectedGlobalOption?.pourcentage_concierge || 70;
                          const pctApp = selectedGlobalOption?.pourcentage_app || 30;
                          const partConcierge = marge * (pctConcierge / 100);
                          const partApp = marge * (pctApp / 100);
                          const aReverser = montantPresta + partConcierge;
                          const isMargeOk = marge >= 5;
                          
                          return (
                            <div className="space-y-2 text-sm">
                              <div className={`flex justify-between ${isMargeOk ? 'text-green-700' : 'text-red-700'}`}>
                                <span>Marge restante :</span>
                                <span className="font-semibold">{marge.toFixed(2)} €</span>
                              </div>
                              {!isMargeOk && (
                                <div className="flex items-center gap-2 text-red-600 text-xs">
                                  <AlertTriangle className="w-4 h-4" />
                                  La marge doit être d'au moins 5 €
                                </div>
                              )}
                              {isMargeOk && (
                                <>
                                  <div className="flex justify-between text-slate-600">
                                    <span>Ma part ({pctConcierge}%) :</span>
                                    <span>{partConcierge.toFixed(2)} €</span>
                                  </div>
                                  <div className="flex justify-between text-slate-600">
                                    <span>Part Plus ({pctApp}%) :</span>
                                    <span>{partApp.toFixed(2)} €</span>
                                  </div>
                                  <div className="flex justify-between font-medium text-purple-700 pt-2 border-t border-purple-100">
                                    <span>À me reverser (prestataire + ma part) :</span>
                                    <span>{aReverser.toFixed(2)} €</span>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Configuration du délai d'achat */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Délai d'achat
                  </Label>
                  <Select
                    value={formData.deadline_type}
                    onValueChange={(value) => setFormData({ ...formData, deadline_type: value, deadline_value: value === 'none' ? '' : formData.deadline_value })}
                  >
                    <SelectTrigger data-testid="deadline-type-select" className="mt-1">
                      <SelectValue placeholder="Sélectionner un délai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune limite</SelectItem>
                      <SelectItem value="hours">Heures avant l'arrivée</SelectItem>
                      <SelectItem value="days">Jours avant l'arrivée</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {formData.deadline_type !== 'none' && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        min="1"
                        placeholder={formData.deadline_type === 'hours' ? 'Nombre d\'heures' : 'Nombre de jours'}
                        value={formData.deadline_value}
                        onChange={(e) => setFormData({ ...formData, deadline_value: e.target.value })}
                        data-testid="deadline-value-input"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {formData.deadline_type === 'hours' 
                          ? `L'option sera disponible jusqu'à ${formData.deadline_value || 'X'} heures avant l'heure de check-in`
                          : `L'option sera disponible jusqu'à ${formData.deadline_value || 'X'} jours avant l'heure de check-in`
                        }
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Question personnalisée pour le voyageur */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Question au voyageur (facultatif)
                  </Label>
                  <Textarea
                    placeholder="Ex : À quelle heure souhaitez-vous votre arrivée anticipée ?"
                    value={formData.question_text}
                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                    data-testid="question-text-input"
                    className="mt-1 bg-white"
                    rows={2}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Si renseignée, cette question sera posée au voyageur lors de la sélection de cette option
                  </p>
                </div>

                {/* Image personnalisée pour l'option */}
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Image personnalisée (facultatif)
                  </Label>
                  <ImageUploader
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    label=""
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Si non renseignée, l'image définie par l'admin sera utilisée
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="actif"
                    checked={formData.actif}
                    onCheckedChange={(checked) => setFormData({ ...formData, actif: checked })}
                    data-testid="option-actif-switch"
                  />
                  <Label htmlFor="actif">Option active</Label>
                </div>
                <Button type="submit" data-testid="option-submit-btn" className="w-full bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
                  Ajouter
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {propertyOptions.length === 0 ? (
          <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-8 sm:p-12 text-center" data-testid="empty-property-options-state">
            <Package className="w-12 sm:w-16 h-12 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" strokeWidth={1.5} />
            <h3 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aucune option configurée</h3>
            <p className="text-sm text-slate-500 mb-4 sm:mb-6">Ajoutez des options pour ce logement</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg">
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Ajouter une option
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="property-options-grid">
            {propertyOptions.map((option) => {
              const globalOpt = option.global_option || {};
              const isPrixFixe = globalOpt.prix_fixe !== null && globalOpt.prix_fixe !== undefined;
              const hasCustomDescription = option.description_custom && option.description_custom.trim() !== '';
              // Priorité : image personnalisée > image globale
              const displayImageUrl = option.image_url || globalOpt.image_url;
              const hasCustomImage = option.image_url && option.image_url.trim() !== '';
              
              return (
                <Card key={option.id} className="bg-white border border-slate-100 shadow-sm rounded-xl overflow-hidden hover-lift" data-testid={`property-option-card-${option.id}`}>
                  {displayImageUrl && (
                    <div className="relative w-full h-32 sm:h-40 overflow-hidden bg-slate-100">
                      <img 
                        src={displayImageUrl} 
                        alt={globalOpt.nom}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {hasCustomImage && (
                        <span className="absolute top-2 right-2 text-xs bg-purple-600 text-white px-2 py-1 rounded flex items-center">
                          <ImageIcon className="w-3 h-3 mr-1" />
                          Perso
                        </span>
                      )}
                    </div>
                  )}
                  
                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 pr-2">
                        {/* Afficher le titre personnalisé pour prestataire, sinon le nom global */}
                        <div className="flex items-center gap-2 mb-1 sm:mb-2">
                          <h3 className="text-base sm:text-lg lg:text-xl font-medium text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {globalOpt.option_type === 'prestataire' && option.titre_personnalise 
                              ? option.titre_personnalise 
                              : globalOpt.nom}
                          </h3>
                          {globalOpt.option_type === 'prestataire' && (
                            <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex-shrink-0">
                              <UserCheck className="w-3 h-3" />
                              Prestataire
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 mb-2 line-clamp-2">
                          {hasCustomDescription ? option.description_custom : globalOpt.description}
                        </p>
                        
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {hasCustomDescription && (
                            <span className="text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded flex items-center">
                              <Edit3 className="w-3 h-3 mr-1" />
                              Perso
                            </span>
                          )}
                          {isPrixFixe && globalOpt.option_type !== 'prestataire' && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center">
                              <Lock className="w-3 h-3 mr-1" />
                              Fixe
                            </span>
                          )}
                        </div>
                      </div>
                      <Switch
                        checked={option.actif}
                        onCheckedChange={() => toggleActif(option.id, option.actif)}
                        data-testid={`option-toggle-${option.id}`}
                        className="flex-shrink-0"
                      />
                    </div>

                    {/* Affichage tarification - différent pour prestataire */}
                    {globalOpt.option_type === 'prestataire' ? (
                      <div className="p-3 bg-purple-50 rounded-lg space-y-2">
                        <div className="flex justify-between items-center">
                          <p className="text-xs text-purple-600">Prix voyageur</p>
                          <p className="text-lg font-bold text-purple-700 tabular-nums">{option.prix} €</p>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <p className="text-slate-600">Montant prestataire</p>
                          <p className="font-medium text-slate-700 tabular-nums">{option.montant_prestataire || 0} €</p>
                        </div>
                        <div className="flex justify-between items-center text-sm border-t border-purple-200 pt-2">
                          <p className="text-purple-700">Ma marge</p>
                          <p className="font-bold text-purple-700 tabular-nums">
                            {((option.prix || 0) - (option.montant_prestataire || 0)).toFixed(2)} € × {globalOpt.pourcentage_concierge}%
                          </p>
                        </div>
                        {option.prestataire_nom && (
                          <div className="text-xs text-slate-500 border-t border-purple-100 pt-2 mt-2">
                            <span className="font-medium">Prestataire :</span> {option.prestataire_nom}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="p-2 sm:p-3 bg-rose-50 rounded-lg">
                        <p className="text-xs text-rose-600 mb-1">Prix</p>
                        <p className="text-xl sm:text-2xl font-bold text-rose-600 tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>{option.prix} €</p>
                      </div>
                    )}

                    {/* Affichage de la question - priorité à la question personnalisée du concierge */}
                    {(option.question_text || (globalOpt.question_enabled && globalOpt.question_text)) && (
                      <div className="p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-600 mb-1 flex items-center">
                          <MessageSquare className="w-3 h-3 mr-1" />
                          Question au voyageur
                          {option.question_text && <span className="ml-1 text-blue-400">(personnalisée)</span>}
                        </p>
                        <p className="text-xs sm:text-sm text-blue-800 italic">
                          "{option.question_text || globalOpt.question_text}"
                        </p>
                      </div>
                    )}

                    {/* Affichage du délai d'achat */}
                    {option.deadline_type && option.deadline_value && (
                      <div className="p-2 sm:p-3 bg-orange-50 rounded-lg border border-orange-100">
                        <p className="text-xs text-orange-600 flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Disponible jusqu'à {option.deadline_value} {option.deadline_type === 'hours' ? 'heure(s)' : 'jour(s)'} avant l'arrivée
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs">
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-slate-500 mb-1">Concierge</p>
                        <p className="font-bold text-slate-700 tabular-nums">{globalOpt.pourcentage_concierge}%</p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <p className="text-slate-500 mb-1">App</p>
                        <p className="font-bold text-slate-700 tabular-nums">{globalOpt.pourcentage_app}%</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(option)}
                        data-testid={`edit-property-option-${option.id}`}
                        className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50 text-xs sm:text-sm"
                      >
                        <Pencil className="w-3 sm:w-4 h-3 sm:h-4 mr-1 sm:mr-2" strokeWidth={1.5} />
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(option.id)}
                        data-testid={`delete-property-option-${option.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Dialog d'édition */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Modifier l'option
              </DialogTitle>
            </DialogHeader>
            {editingOption && (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {editingOption.global_option?.option_type === 'prestataire' && (
                      <UserCheck className="w-4 h-4 text-purple-600" />
                    )}
                    {editingOption.global_option?.nom}
                    {editingOption.global_option?.option_type === 'prestataire' && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Prestataire</span>
                    )}
                  </p>
                </div>

                {/* Section prestataire si option de type prestataire */}
                {editingOption.global_option?.option_type === 'prestataire' && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                    <div>
                      <Label htmlFor="edit-titre-personnalise" className="text-purple-800">
                        Titre affiché au voyageur *
                      </Label>
                      <Input
                        id="edit-titre-personnalise"
                        value={editTitrePersonnalise}
                        onChange={(e) => setEditTitrePersonnalise(e.target.value)}
                        required
                        data-testid="edit-titre-personnalise-input"
                        placeholder="Ex: Accrobranche au parc X"
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-purple-200">
                      <div>
                        <Label htmlFor="edit-prestataire-nom">Nom du prestataire</Label>
                        <Input
                          id="edit-prestataire-nom"
                          value={editPrestataireNom}
                          onChange={(e) => setEditPrestataireNom(e.target.value)}
                          data-testid="edit-prestataire-nom-input"
                          placeholder="Nom"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-prestataire-email">Email</Label>
                        <Input
                          id="edit-prestataire-email"
                          type="email"
                          value={editPrestataireEmail}
                          onChange={(e) => setEditPrestataireEmail(e.target.value)}
                          data-testid="edit-prestataire-email-input"
                          placeholder="Email"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-prestataire-telephone">Téléphone</Label>
                        <Input
                          id="edit-prestataire-telephone"
                          type="tel"
                          value={editPrestataireTelephone}
                          onChange={(e) => setEditPrestataireTelephone(e.target.value)}
                          data-testid="edit-prestataire-telephone-input"
                          placeholder="Téléphone"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-prestataire-adresse">Adresse</Label>
                        <Input
                          id="edit-prestataire-adresse"
                          value={editPrestataireAdresse}
                          onChange={(e) => setEditPrestataireAdresse(e.target.value)}
                          data-testid="edit-prestataire-adresse-input"
                          placeholder="Facultatif"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-note-interne">Note interne</Label>
                      <Textarea
                        id="edit-note-interne"
                        value={editNoteInterne}
                        onChange={(e) => setEditNoteInterne(e.target.value)}
                        data-testid="edit-note-interne-input"
                        placeholder="Note visible uniquement par vous"
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                  </div>
                )}

                {/* Champ Prix */}
                <div>
                  <Label htmlFor="edit-prix" className="flex items-center">
                    {editingOption.global_option?.option_type === 'prestataire' ? 'Prix payé par le voyageur (€)' : 'Prix (€)'}
                    {editingOption.global_option?.prix_fixe && editingOption.global_option?.option_type !== 'prestataire' && (
                      <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center">
                        <Lock className="w-3 h-3 mr-1" />
                        Prix fixe
                      </span>
                    )}
                  </Label>
                  <Input
                    id="edit-prix"
                    type="number"
                    step="0.01"
                    min="5"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    required
                    disabled={editingOption.global_option?.prix_fixe && editingOption.global_option?.option_type !== 'prestataire'}
                    data-testid="edit-option-prix-input"
                    className="mt-1"
                  />
                  {editingOption.global_option?.prix_fixe && editingOption.global_option?.option_type !== 'prestataire' ? (
                    <p className="text-xs text-amber-600 mt-1">
                      Prix fixé par l'administration
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1">
                      {editingOption.global_option?.option_type === 'prestataire' 
                        ? "Prix total que le voyageur va payer"
                        : "Prix minimum : 5€"}
                    </p>
                  )}
                </div>

                {/* Montant prestataire pour les options prestataire */}
                {editingOption.global_option?.option_type === 'prestataire' && (
                <>
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Label htmlFor="edit-montant-prestataire" className="text-purple-800">
                      Montant dû au prestataire (€) *
                    </Label>
                    <Input
                      id="edit-montant-prestataire"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editMontantPrestataire}
                      onChange={(e) => setEditMontantPrestataire(e.target.value)}
                      required
                      data-testid="edit-montant-prestataire-input"
                      className="mt-1"
                    />
                    
                    {/* Calcul automatique de la marge */}
                    {editPrice && editMontantPrestataire && (
                      <div className="mt-3 p-2 bg-white rounded border border-purple-100">
                        {(() => {
                          const prix = parseFloat(editPrice) || 0;
                          const montantPresta = parseFloat(editMontantPrestataire) || 0;
                          const marge = prix - montantPresta;
                          const pctConcierge = editingOption.global_option?.pourcentage_concierge || 70;
                          const pctApp = editingOption.global_option?.pourcentage_app || 30;
                          const partConcierge = marge * (pctConcierge / 100);
                          const partApp = marge * (pctApp / 100);
                          const aReverser = montantPresta + partConcierge;
                          const isMargeOk = marge >= 5;
                          
                          return (
                            <div className="space-y-1 text-xs">
                              <div className={`flex justify-between ${isMargeOk ? 'text-green-700' : 'text-red-700'}`}>
                                <span>Marge :</span>
                                <span className="font-semibold">{marge.toFixed(2)} €</span>
                              </div>
                              {!isMargeOk && (
                                <div className="flex items-center gap-1 text-red-600">
                                  <AlertTriangle className="w-3 h-3" />
                                  Min. 5 €
                                </div>
                              )}
                              {isMargeOk && (
                                <>
                                  <div className="flex justify-between text-slate-600">
                                    <span>Ma part ({pctConcierge}%) :</span>
                                    <span>{partConcierge.toFixed(2)} €</span>
                                  </div>
                                  <div className="flex justify-between text-purple-700 font-medium border-t border-purple-100 pt-1">
                                    <span>À me reverser :</span>
                                    <span>{aReverser.toFixed(2)} €</span>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  
                  {/* Type de tarification */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Label className="text-blue-800 mb-2 block">Type de tarification</Label>
                    <Select value={editTypeTarif} onValueChange={setEditTypeTarif}>
                      <SelectTrigger data-testid="edit-type-tarif-select" className="bg-white">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personne">Par personne (prix × nombre de participants)</SelectItem>
                        <SelectItem value="groupe">Par groupe (prix fixe)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-600 mt-1">
                      {editTypeTarif === 'personne' 
                        ? 'Le prix sera multiplié par le nombre de participants' 
                        : 'Le prix est fixe peu importe le nombre de participants'}
                    </p>
                    
                    {/* Participants min/max */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <Label htmlFor="edit-participants-min" className="text-xs text-blue-700">
                          Min. participants
                        </Label>
                        <Input
                          id="edit-participants-min"
                          type="number"
                          min="1"
                          value={editParticipantsMin}
                          onChange={(e) => setEditParticipantsMin(e.target.value)}
                          placeholder="Aucun"
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-participants-max" className="text-xs text-blue-700">
                          Max. participants
                        </Label>
                        <Input
                          id="edit-participants-max"
                          type="number"
                          min="1"
                          value={editParticipantsMax}
                          onChange={(e) => setEditParticipantsMax(e.target.value)}
                          placeholder="Aucun"
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                </>
                )}

                {/* Champ Description personnalisée */}
                {editingOption.global_option?.description_editable && (
                  <div>
                    <Label htmlFor="edit-description" className="flex items-center">
                      <Edit3 className="w-4 h-4 mr-2 text-rose-600" />
                      Description personnalisée
                    </Label>
                    <Textarea
                      id="edit-description"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      data-testid="edit-option-description-input"
                      placeholder="Personnalisez la description..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                )}

                {/* Configuration du délai d'achat */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <Clock className="w-4 h-4 mr-2" />
                    Délai d'achat
                  </Label>
                  <Select
                    value={editDeadlineType}
                    onValueChange={(value) => {
                      setEditDeadlineType(value);
                      if (value === 'none') setEditDeadlineValue('');
                    }}
                  >
                    <SelectTrigger data-testid="edit-deadline-type-select" className="mt-1">
                      <SelectValue placeholder="Sélectionner un délai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune limite</SelectItem>
                      <SelectItem value="hours">Heures avant l'arrivée</SelectItem>
                      <SelectItem value="days">Jours avant l'arrivée</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {editDeadlineType !== 'none' && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        min="1"
                        placeholder={editDeadlineType === 'hours' ? 'Nombre d\'heures' : 'Nombre de jours'}
                        value={editDeadlineValue}
                        onChange={(e) => setEditDeadlineValue(e.target.value)}
                        data-testid="edit-deadline-value-input"
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        {editDeadlineType === 'hours' 
                          ? `Disponible jusqu'à ${editDeadlineValue || 'X'}h avant le check-in`
                          : `Disponible jusqu'à ${editDeadlineValue || 'X'} jour(s) avant le check-in`
                        }
                      </p>
                    </div>
                  )}
                </div>

                {/* Question personnalisée pour le voyageur */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Question au voyageur (facultatif)
                  </Label>
                  <Textarea
                    placeholder="Ex : À quelle heure souhaitez-vous votre arrivée anticipée ?"
                    value={editQuestionText}
                    onChange={(e) => setEditQuestionText(e.target.value)}
                    data-testid="edit-question-text-input"
                    className="mt-1 bg-white"
                    rows={2}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Si renseignée, cette question sera posée au voyageur lors de la sélection
                  </p>
                </div>

                {/* Image personnalisée pour l'option */}
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Label className="flex items-center text-slate-700 mb-2">
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Image personnalisée (facultatif)
                  </Label>
                  {editImageUrl && (
                    <div className="relative mb-2">
                      <img src={editImageUrl} alt="Image option" className="w-full h-32 object-cover rounded-lg" />
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="absolute top-2 right-2 h-7 w-7 p-0"
                        onClick={() => setEditImageUrl('')}
                        data-testid="remove-option-image-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                      <p className="text-xs text-slate-500 mt-1">
                        Cliquez sur ✕ pour supprimer et revenir à l'image globale
                      </p>
                    </div>
                  )}
                  {!editImageUrl && (
                    <ImageUploader
                      value={editImageUrl}
                      onChange={(url) => setEditImageUrl(url)}
                      label=""
                    />
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    Si non renseignée, l'image définie par l'admin sera utilisée
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditDialogOpen(false)}
                    className="flex-1"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    data-testid="edit-option-submit-btn"
                    className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                  >
                    Enregistrer
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PropertyOptions;
